import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  Paper,
  ActionIcon,
  Collapse,
  Badge,
  Tooltip,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconUsers,
} from '@tabler/icons-react';
import { Card } from '../../ui';
import { FadeIn, SlideIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface OrgNode {
  id: string;
  name: string;
  title?: string;
  department?: string;
  avatar?: string;
  email?: string;
  children?: OrgNode[];
  metadata?: Record<string, unknown>;
}

export interface OrgChartProps {
  data: OrgNode;
  variant?: 'tree' | 'horizontal' | 'compact';
  onNodeClick?: (node: OrgNode) => void;
  expandedByDefault?: boolean;
  maxDepth?: number;
  showCount?: boolean;
  renderNode?: (node: OrgNode, depth: number) => ReactNode;
  className?: string;
}

export const OrgChart = forwardRef<HTMLDivElement, OrgChartProps>(
  (
    {
      data,
      variant = 'tree',
      onNodeClick,
      expandedByDefault = true,
      maxDepth = 10,
      showCount = true,
      renderNode,
      className,
    },
    ref
  ) => {
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
      expandedByDefault ? new Set([data.id]) : new Set()
    );

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const toggleNode = (id: string) => {
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    };

    const countDescendants = (node: OrgNode): number => {
      if (!node.children || node.children.length === 0) return 0;
      return node.children.reduce(
        (sum, child) => sum + 1 + countDescendants(child),
        0
      );
    };

    const renderDefaultNode = (node: OrgNode, depth: number) => (
      <Paper
        p="sm"
        withBorder
        style={{
          cursor: onNodeClick ? 'pointer' : 'default',
          minWidth: 200,
        }}
        onClick={() => onNodeClick?.(node)}
      >
        <Group gap="sm">
          <Avatar src={node.avatar} size="md" radius="xl">
            {node.name.charAt(0)}
          </Avatar>
          <Box style={{ flex: 1, overflow: 'hidden' }}>
            <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
              {node.name}
            </Text>
            {node.title && (
              <Text size="xs" c="dimmed" lineClamp={1} style={accessibilityStyles}>
                {node.title}
              </Text>
            )}
            {node.department && (
              <Badge size="xs" variant="light" mt={4}>
                {node.department}
              </Badge>
            )}
          </Box>
          {showCount && node.children && node.children.length > 0 && (
            <Tooltip label={`${countDescendants(node)} team members`}>
              <Badge size="xs" variant="outline" leftSection={<IconUsers size={10} />}>
                {node.children.length}
              </Badge>
            </Tooltip>
          )}
        </Group>
      </Paper>
    );

    const renderTreeNode = (node: OrgNode, depth: number): ReactNode => {
      if (depth > maxDepth) return null;

      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodes.has(node.id);

      return (
        <Box key={node.id}>
          <SlideIn direction="left" delay={depth * 50}>
            <Group gap="xs" mb="xs">
              {hasChildren && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id);
                  }}
                >
                  {isExpanded ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronRight size={14} />
                  )}
                </ActionIcon>
              )}
              {!hasChildren && <Box w={22} />}
              {renderNode ? renderNode(node, depth) : renderDefaultNode(node, depth)}
            </Group>
          </SlideIn>

          {hasChildren && (
            <Collapse in={isExpanded}>
              <Box
                ml={40}
                pl="md"
                style={{
                  borderLeft: '2px solid var(--mantine-color-gray-3)',
                }}
              >
                {node.children?.map((child) => renderTreeNode(child, depth + 1))}
              </Box>
            </Collapse>
          )}
        </Box>
      );
    };

    const renderHorizontalNode = (node: OrgNode, depth: number): ReactNode => {
      if (depth > maxDepth) return null;

      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodes.has(node.id);

      return (
        <Box key={node.id}>
          <Group gap="md" align="flex-start">
            <Box style={{ position: 'relative' }}>
              {renderNode ? renderNode(node, depth) : renderDefaultNode(node, depth)}
              {hasChildren && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  style={{
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(node.id);
                  }}
                >
                  {isExpanded ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronRight size={14} />
                  )}
                </ActionIcon>
              )}
            </Box>
          </Group>

          {hasChildren && isExpanded && (
            <Box mt="md">
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {/* Vertical line from parent */}
                <Box
                  style={{
                    position: 'absolute',
                    top: -8,
                    left: '50%',
                    width: 2,
                    height: 16,
                    backgroundColor: 'var(--mantine-color-gray-3)',
                  }}
                />
                {/* Horizontal line connecting children */}
                {node.children && node.children.length > 1 && (
                  <Box
                    style={{
                      position: 'absolute',
                      top: 8,
                      height: 2,
                      backgroundColor: 'var(--mantine-color-gray-3)',
                      left: `calc(50% / ${node.children.length})`,
                      right: `calc(50% / ${node.children.length})`,
                    }}
                  />
                )}
              </Box>
              <Group justify="center" gap="xl" mt="md">
                {node.children?.map((child) => (
                  <Box key={child.id}>
                    {/* Vertical line to child */}
                    <Box
                      style={{
                        width: 2,
                        height: 16,
                        backgroundColor: 'var(--mantine-color-gray-3)',
                        margin: '0 auto',
                      }}
                    />
                    {renderHorizontalNode(child, depth + 1)}
                  </Box>
                ))}
              </Group>
            </Box>
          )}
        </Box>
      );
    };

    const renderCompactNode = (node: OrgNode, depth: number): ReactNode => {
      if (depth > maxDepth) return null;

      const hasChildren = node.children && node.children.length > 0;
      const isExpanded = expandedNodes.has(node.id);

      return (
        <Box key={node.id}>
          <Paper
            p="xs"
            withBorder
            mb={4}
            style={{
              cursor: 'pointer',
              marginLeft: depth * 20,
              backgroundColor: depth === 0 ? 'var(--mantine-color-blue-0)' : undefined,
            }}
            onClick={() => {
              if (hasChildren) {
                toggleNode(node.id);
              }
              onNodeClick?.(node);
            }}
          >
            <Group gap="xs">
              {hasChildren && (
                isExpanded ? (
                  <IconChevronDown size={12} />
                ) : (
                  <IconChevronRight size={12} />
                )
              )}
              {!hasChildren && <Box w={12} />}
              <Avatar src={node.avatar} size="xs" radius="xl">
                {node.name.charAt(0)}
              </Avatar>
              <Text size="sm" fw={depth === 0 ? 600 : 400} style={accessibilityStyles}>
                {node.name}
              </Text>
              {node.title && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>• {node.title}</Text>
              )}
              {showCount && hasChildren && (
                <Badge size="xs" variant="light" ml="auto">
                  {countDescendants(node)}
                </Badge>
              )}
            </Group>
          </Paper>

          {hasChildren && isExpanded && (
            <Box>
              {node.children?.map((child) => renderCompactNode(child, depth + 1))}
            </Box>
          )}
        </Box>
      );
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          {variant === 'tree' && renderTreeNode(data, 0)}
          {variant === 'horizontal' && (
            <Box style={{ overflowX: 'auto' }}>
              <Box style={{ display: 'inline-block', minWidth: 'fit-content' }}>
                {renderHorizontalNode(data, 0)}
              </Box>
            </Box>
          )}
          {variant === 'compact' && renderCompactNode(data, 0)}
        </Card>
      </FadeIn>
    );
  }
);

OrgChart.displayName = 'OrgChart';

export default OrgChart;
